import useStore from "../store.ts";
import type {Component} from "../types.ts";
import {RuleType} from "../rule/RuleEntry.ts";

export type ChannelFieldMap = Map<number, Set<number>>;

const toChannelId = (value: number | null | undefined): number | null => {
    if (value == null) return null;
    const channel = Number(value);
    if (!Number.isFinite(channel) || channel <= 0) return null;
    return channel;
};

const toFieldId = (value: number | null | undefined): number | null => {
    if (value == null) return null;
    const field = Number(value);
    if (!Number.isFinite(field) || field < 0) return null;
    return field;
};

const addField = (map: ChannelFieldMap, channel: number | null | undefined, field: number | null | undefined): void => {
    const channelId = toChannelId(channel);
    const fieldId = toFieldId(field);
    if (channelId == null || fieldId == null) return;

    let set = map.get(channelId);
    if (!set) {
        set = new Set<number>();
        map.set(channelId, set);
    }
    set.add(fieldId);
};

/* see docs/EXTRACTED_COMMENTS.md#c774 */
const getRuleChannels = (component: Component): number[] => {
    const dyn = component.dynamic;
    if (!dyn) return [];
    const channels: number[] = [];
    for (const candidate of [toChannelId(dyn.key_channel), toChannelId(dyn.channel)]) {
        if (candidate != null && !channels.includes(candidate)) channels.push(candidate);
    }
    return channels;
};

export type ChannelFieldRef = readonly [channel: number | null | undefined, field: number | null | undefined];

/* see docs/EXTRACTED_COMMENTS.md#c770 */
export function collectComponentFieldRefs(component: Component): ChannelFieldRef[] {
    const refs: ChannelFieldRef[] = [];
    const dyn = component.dynamic;
    if (!dyn) return refs;

    if (dyn.field) {
        // see docs/EXTRACTED_COMMENTS.md#c771
        const field = dyn.field.field;
        refs.push(
            [dyn.channel, field],
            [dyn.key_channel, field],
            [dyn.dynamic_channel, field],
            [dyn.dynamic_key_channel, field],
        );
    }

    for (const df of dyn.dynamic_fields ?? []) {
        // The match keys are compared against dynamic-channel rows in getDynamicValue.
        const field = df?.dynamic_field?.field;
        refs.push([dyn.dynamic_channel, field], [dyn.dynamic_key_channel, field]);

        // getSourceValue/computeSourceValue recurses with key_channel/channel only.
        if (df?.source_field) {
            refs.push([dyn.channel, df.source_field.field], [dyn.key_channel, df.source_field.field]);
        }
    }

    const fp = component.function_props;
    if (fp?.second_field != null) {
        refs.push([fp.second_channel ?? dyn.key_channel ?? dyn.channel, fp.second_field]);
    }

    return refs;
}

export function collectChannelFields(): ChannelFieldMap {
    const store = useStore();
    const map: ChannelFieldMap = new Map();

    for (const component of Object.values(store.components) as Component[]) {
        for (const [channel, field] of collectComponentFieldRefs(component)) {
            addField(map, channel, field);
        }
    }

    return map;
}

export function collectRuleNames(): Set<string> {
    const store = useStore();
    const names = new Set<string>();

    for (const component of Object.values(store.components) as Component[]) {
        if (component.decision_props?.rule) {
            names.add(component.decision_props.rule);
        }
        if (component.container_props?.rule_maps) {
            for (const rm of component.container_props.rule_maps) {
                if (rm?.rule) {
                    names.add(rm.rule);
                }
            }
        }
    }

    return names;
}

/* see docs/EXTRACTED_COMMENTS.md#c784 */
export async function prefetchRules(ruleNames: Set<string>, idleEscapeMs = 1_000): Promise<void> {
    const store = useStore();
    if (ruleNames.size === 0) return;

    let settled = 0;
    const waiters: Promise<void>[] = [];
    for (const name of ruleNames) {
        if (store.ruleManager.isMissing(name)) continue;
        waiters.push(store.ruleManager.requestRuleWithPromise(name).then(() => { settled++; }));
        store.ruleManager.addRule(name);
        store.socketManager.commandQueue.send("request_rule", {rule_id: name});
    }
    if (waiters.length === 0) return;

    const started = performance.now();
    const complete = Promise.all(waiters).then(() => true);
    let seen = 0;
    for (;;) {
        let idleTimer: number | undefined;
        const done = await Promise.race([
            complete,
            new Promise<boolean>((resolve) => {
                idleTimer = window.setTimeout(() => resolve(false), idleEscapeMs);
            }),
        ]);
        if (idleTimer !== undefined) window.clearTimeout(idleTimer);
        if (done) return;
        // Replies are still landing — give the pipe another idle window.
        if (settled === seen) break;
        seen = settled;
    }

    const unresolved = [...ruleNames].filter((name) =>
        !store.ruleManager.getRule(name)?.getValue() && !store.ruleManager.isMissing(name));
    console.warn(
        `[ChannelFieldCollector] Rule prefetch escaped after ${Math.round(performance.now() - started)}ms ` +
        `(${idleEscapeMs}ms idle) with no reply for: ${unresolved.join(", ") || "(none)"} — ` +
        `subscribing without their fields.`
    );
}

export function buildRuleFieldMap(): Map<string, number[]> {
    const store = useStore();
    const result = new Map<string, number[]>();

    for (const [name, rule] of store.ruleManager.getRules()) {
        const entries = rule.getValue();
        if (!entries) continue;

        const fieldIds = new Set<number>();
        for (const entry of entries) {
            if (entry.getRuleType() !== RuleType.FIELD) continue;
            const raw = `${entry.getField() ?? ""}`;
            const numeric = Number(raw.replace(/\[.*\]/, ""));
            if (Number.isFinite(numeric) && numeric >= 0) {
                fieldIds.add(numeric);
            }
        }

        if (fieldIds.size > 0) {
            result.set(name, Array.from(fieldIds));
        }
    }

    return result;
}

export function getRuleFieldIds(ruleName: string): number[] {
    const store = useStore();
    const rule = store.ruleManager.getRule(ruleName);
    const entries = rule?.getValue();
    if (!entries) return [];

    const fieldIds = new Set<number>();
    for (const entry of entries) {
        if (entry.getRuleType() !== RuleType.FIELD) continue;
        const raw = `${entry.getField() ?? ""}`;
        const numeric = Number(raw.replace(/\[.*\]/, ""));
        if (Number.isFinite(numeric) && numeric >= 0) {
            fieldIds.add(numeric);
        }
    }

    return Array.from(fieldIds);
}

export function mergeRuleFields(fieldMap: ChannelFieldMap, ruleFieldIds: Map<string, number[]>): void {
    const store = useStore();
    for (const component of Object.values(store.components) as Component[]) {
        const channels = getRuleChannels(component);
        if (channels.length === 0) continue;

        const ruleNames: string[] = [];
        const decisionRule = component.decision_props?.rule;
        if (decisionRule) ruleNames.push(decisionRule);
        for (const rm of component.container_props?.rule_maps ?? []) {
            if (rm?.rule) ruleNames.push(rm.rule);
        }

        for (const ruleName of ruleNames) {
            for (const fieldId of ruleFieldIds.get(ruleName) ?? []) {
                for (const channel of channels) {
                    addField(fieldMap, channel, fieldId);
                }
            }
        }
    }
}

export function getChannelSubscriptionFields(channelName: string | number): number[] {
    const store = useStore();
    const channel = toChannelId(Number(channelName));
    if (channel == null) return [];

    const fieldSet = store.channelFieldMap.get(channel);
    if (fieldSet && fieldSet.size > 0) {
        return Array.from(fieldSet).sort((a, b) => a - b);
    }

    // see docs/EXTRACTED_COMMENTS.md#c547
    const rebuiltMap = collectChannelFields();
    const rebuiltRuleMap = buildRuleFieldMap();
    mergeRuleFields(rebuiltMap, rebuiltRuleMap);

    store.channelFieldMap.clear();
    rebuiltMap.forEach((set, channelId) => {
        store.channelFieldMap.set(channelId, new Set(set));
    });

    const rebuiltSet = store.channelFieldMap.get(channel);
    if (rebuiltSet && rebuiltSet.size > 0) {
        return Array.from(rebuiltSet).sort((a, b) => a - b);
    }

    console.warn(`[ChannelFieldCollector] No field map entry for channel ${channel} after rebuild; requesting empty field set.`);
    return [];
}

/* see docs/EXTRACTED_COMMENTS.md#c761 */
export function widenChannelFieldsForRule(ruleName: string, addedFieldIds: number[]): void {
    if (addedFieldIds.length === 0) return;
    const store = useStore();

    const affectedChannels = new Set<number>();
    for (const component of Object.values(store.components) as Component[]) {
        const usesDecisionRule = component.decision_props?.rule === ruleName;
        const usesMappedRule = component.container_props?.rule_maps?.some((rm) => rm.rule === ruleName) ?? false;
        if (!usesDecisionRule && !usesMappedRule) continue;

        for (const channel of getRuleChannels(component)) {
            affectedChannels.add(channel);
        }
    }

    for (const channel of affectedChannels) {
        let existing = store.channelFieldMap.get(channel);
        if (!existing) {
            existing = new Set<number>();
            store.channelFieldMap.set(channel, existing);
        }

        for (const fieldId of addedFieldIds) {
            const normalizedField = toFieldId(fieldId);
            if (normalizedField == null) continue;
            existing.add(normalizedField);
        }
    }
}
